import { GetStagesItem } from "@microtica/ms-engine-sdk";
import { Button, Divider, Input } from "antd";
import { useEffect, useState } from "react"
import { LayoutGrid, Search, Plus, Cloud } from "lucide-react";
import { useParams } from "react-router-dom";
import CreateEnvironmentModal from "./CreateEnvironmentModal";
import { getCloudService, getEnvironmentService } from "../../backend";
import EnvironmentCard from "../../components/cards/EnvironmentCard";
import CardGridSkeleton from "../../components/cards/CardGridSkeleton";
import EmptyState from "../../components/EmptyState";
import { CloudAccountMap } from "../../types";

const ProjectEnvironments = () => {
    const { projectId } = useParams() as { projectId: string; };
    const [loading, setLoading] = useState(true);
    const [environments, setEnvironments] = useState<GetStagesItem[]>([]);
    const [filteredEnvironments, setFilteredEnvironments] = useState<GetStagesItem[]>([]);
    const [searchText, setSearchText] = useState("");
    const [createEnvironmentModalVisible, setCreateEnvironmentModalVisible] = useState(false);
    const [cloudAccountNames, setCloudAccountNames] = useState<CloudAccountMap>({});

    useEffect(() => {
        loadData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (searchText) {
            const filtered = environments.filter(env =>
                env.name.toLowerCase().includes(searchText.toLowerCase()) ||
                env.description?.toLowerCase().includes(searchText.toLowerCase()) ||
                env.awsAccountId?.toLowerCase().includes(searchText.toLowerCase()) ||
                env.awsRegion?.toLowerCase().includes(searchText.toLowerCase()) ||
                env.status?.toLowerCase().includes(searchText.toLowerCase())
            );
            setFilteredEnvironments(filtered);
        } else {
            setFilteredEnvironments(environments);
        }
    }, [environments, searchText]);

    const loadData = async () => {
        setLoading(true);
        try {
            const { data: { stages } } = await getEnvironmentService().getStages(projectId);
            setEnvironments(stages);
            setFilteredEnvironments(stages);
            
            // Load cloud account names
            try {
                const [awsResult, gcpResult, exoscaleResult] = await Promise.allSettled([
                    getCloudService().getAwsAccounts(projectId),
                    getCloudService().getGcpAccounts(projectId),
                    getCloudService().getExoscaleAccounts(projectId)
                ]);

                const accountMap: CloudAccountMap = {};

                if (awsResult.status === 'fulfilled') {
                    awsResult.value.data.awsAccounts.forEach(acc => {
                        accountMap[acc.id] = acc.accountName || acc.id;
                    });
                }
                if (gcpResult.status === 'fulfilled') {
                    gcpResult.value.data.gcpAccounts.forEach(acc => {
                        accountMap[acc.gcpProjectId] = acc.projectName || acc.gcpProjectId;
                    });
                }
                if (exoscaleResult.status === 'fulfilled') {
                    exoscaleResult.value.data.exoscaleAccounts.forEach(acc => {
                        if (acc.id) {
                            accountMap[acc.id] = acc.accountName || acc.id;
                        }
                    });
                }

                setCloudAccountNames(accountMap);
            } catch (error) {
                console.error('Error loading cloud accounts:', error);
            }
        } catch (error) {
            console.error('Error loading environments:', error);
        } finally {
            setLoading(false);
        }
    }

    return (
        <>
            <div style={{ margin: 0, padding: 0, width: '100%', height: 'calc(100vh - 56px)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                {/* Custom Header - matching ProjectPipelines design */}
                <div style={{ 
                    display: 'flex', 
                    flexDirection: 'column',
                    backgroundColor: "#ffffff",
                    borderBottom: "1px solid #f0f0f0",
                    flexShrink: 0,
                    padding: "32px 50px 24px 50px"
                }}>
                    <div style={{ 
                        display: "flex", 
                        alignItems: "center",
                        justifyContent: "space-between",
                        padding: "0",
                        background: "transparent",
                        gap: "32px"
                    }}>
                        <div style={{ display: "flex", alignItems: "center", gap: "24px", flex: 1 }}>
                            <div style={{ display: "flex", alignItems: "center", gap: "16px", flexShrink: 0 }}>
                                <div style={{ 
                                    width: "48px", 
                                    height: "48px", 
                                    borderRadius: "12px", 
                                    background: "linear-gradient(135deg, #077BB5 0%, #0a5a82 100%)", 
                                    display: "flex", 
                                    alignItems: "center", 
                                    justifyContent: "center",
                                    boxShadow: "0 4px 12px rgba(7, 123, 181, 0.2)"
                                }}>
                                    <LayoutGrid size={24} color="#fff" />
                                </div>
                                <div style={{ display: "flex", flexDirection: "column" }}>
                                    <span style={{ fontSize: "20px", fontWeight: 600, color: "#111827", margin: "0 0 4px 0", letterSpacing: "-0.02em" }}>Environments</span>
                                    <span style={{ fontSize: "14px", color: "#6B7280", margin: 0 }}>
                                        {loading ? 'Loading...' : `${environments.length} ${environments.length === 1 ? 'environment' : 'environments'}`}
                                    </span>
                                </div>
                            </div>

                            <Divider type="vertical" style={{ height: "32px", margin: "0 4px" }} />

                            {!loading && environments.length > 0 && (
                                <div style={{ flex: 1, maxWidth: "400px" }}>
                                    <Input
                                        placeholder="Search environments..."
                                        prefix={<Search size={16} style={{ color: '#9CA3AF' }} />}
                                        allowClear
                                        size="large"
                                        value={searchText}
                                        onChange={(e) => setSearchText(e.target.value)}
                                    />
                                </div>
                            )}
                        </div>

                        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                            <Button
                                onClick={() => setCreateEnvironmentModalVisible(true)}
                                icon={<Plus size={14} />}
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "6px",
                                    fontWeight: 500
                                }}
                            >
                                Create Environment
                            </Button>
                        </div>
                    </div>
                </div>

                {/* Content Area */}
                <div style={{ flex: 1, overflow: 'auto', padding: '50px 50px' }}>
                    {loading ? (
                        <CardGridSkeleton variant="environment" />
                    ) : (
                        <>
                            {environments.length > 0 && (
                                <>
                                    <div className="cards-grid">
                                        {filteredEnvironments.map((env) => (
                                            <EnvironmentCard 
                                                key={env.id} 
                                                environment={env} 
                                                cloudAccountNames={cloudAccountNames}
                                            />
                                        ))}
                                    </div>
                                    {filteredEnvironments.length === 0 && searchText && (
                                        <EmptyState
                                            icon={<Search size={48} />}
                                            title="No environments found"
                                            description={`No environments match your search "${searchText}". Try a different search term.`}
                                        />
                                    )}
                                </>
                            )}

                            {environments.length === 0 && (
                                <EmptyState
                                    icon={<Cloud size={48} />}
                                    title="Welcome to your environments!"
                                    description="Environments are isolated spaces where your infrastructure and applications live. Create your first environment to get started."
                                    actionText="Create Environment"
                                    onAction={() => setCreateEnvironmentModalVisible(true)}
                                />
                            )}
                        </>
                    )}
                </div>
            </div>

            {createEnvironmentModalVisible && (
                <CreateEnvironmentModal
                    visible={createEnvironmentModalVisible}
                    onCancel={() => setCreateEnvironmentModalVisible(false)}
                    onOk={async () => {
                        await loadData();
                        setCreateEnvironmentModalVisible(false);
                    }}
                />
            )}
        </>
    );
};

export default ProjectEnvironments;
